import { fetcher, queryString, useRecoilQuery } from '@shared/hooks/recoil-query';
import { LowerRole } from '@shared/types';

import {
  giftCouponByCodeDataAtomFamily,
  giftCouponByCodeStatusAtomFamily,
  giftCouponByIdDataAtomFamily,
  giftCouponByIdStatusAtomFamily,
  giftCouponSearchDataAtomFamily,
  giftCouponSearchStatusAtomFamily,
  giftCouponStatisticsDataAtom,
  giftCouponStatisticsStatusAtom,
  redemptionDashboardDataAtom,
  redemptionDashboardStatusAtom,
} from './giftCoupon.atom';
import { GiftCouponSearchParams } from './giftCoupon.interface';

// 코드로 쿠폰 조회 URL (public)
const giftCouponByCodeUrl = (code: string) => {
  return `/${LowerRole.Public}/gift-coupon/code/${code}`;
};

export const useGiftCouponByCodeHook = (code: string, skip = false) => {
  useRecoilQuery(
    giftCouponByCodeStatusAtomFamily(code),
    giftCouponByCodeDataAtomFamily(code),
    giftCouponByCodeUrl(code),
    fetcher,
    skip || !code,
  );
};

// ID로 쿠폰 조회 URL (public)
const giftCouponByIdUrl = (id: string) => {
  return `/${LowerRole.Public}/gift-coupon/${id}`;
};

export const useGiftCouponByIdHook = (id: string, skip = false) => {
  useRecoilQuery(
    giftCouponByIdStatusAtomFamily(id),
    giftCouponByIdDataAtomFamily(id),
    giftCouponByIdUrl(id),
    fetcher,
    skip || !id,
  );
};

const giftCouponPopulate = [
  {
    path: 'giftItem',
    populate: [
      { path: 'image' },
      {
        path: 'giftItemInfo',
        populate: [{ path: 'image' }, { path: 'giftItemBrand' }],
      },
    ],
  },
  {
    path: 'selectedChoiceUnit',
    populate: [{ path: 'image' }, { path: 'giftItemBrand' }],
  },
  {
    path: 'giftOrder',
    select: 'paymentDetail.purchasedAt bugo',
    populate: [
      {
        path: 'bugo',
        select: 'member',
        populate: [{ path: 'member', select: 'fullName phoneNumber' }],
      },
    ],
  },
  { path: 'recipient.signatureImage' },
];

// 발송대기 대시보드 조회 URL (admin)
export const redemptionDashboardUrl = `/${LowerRole.Admin}/gift-coupon/redemption-dashboard`;

export const useRedemptionDashboardHook = (skip = false) => {
  useRecoilQuery(
    redemptionDashboardStatusAtom,
    redemptionDashboardDataAtom,
    redemptionDashboardUrl,
    fetcher,
    skip,
  );
};

// 쿠폰 통계 조회 URL (admin)
export const giftCouponStatisticsUrl = `/${LowerRole.Admin}/gift-coupon/statistics`;

export const useGiftCouponStatisticsHook = (skip = false) => {
  useRecoilQuery(
    giftCouponStatisticsStatusAtom,
    giftCouponStatisticsDataAtom,
    giftCouponStatisticsUrl,
    fetcher,
    skip,
  );
};

// 쿠폰 검색 URL (admin) - 내역 검색 페이지용
const giftCouponSearchUrl = (params: GiftCouponSearchParams) => {
  const filter: Record<string, unknown> = {};

  if (params.redeemStatus) {
    filter.redeemStatus = params.redeemStatus;
  }

  if (params.shareStatus) {
    filter.shareStatus = params.shareStatus;
  }

  if (params.phoneNumber) {
    filter['recipient.phoneNumber'] = { $regex: params.phoneNumber };
  }

  if (params.fullCode) {
    filter.code = { $regex: params.fullCode, $options: 'i' };
  }

  if (params.dateFrom || params.dateTo) {
    const dateFilter: Record<string, string> = {};
    if (params.dateFrom) {
      dateFilter.$gte = new Date(`${params.dateFrom}T00:00:00+09:00`).toISOString();
    }
    if (params.dateTo) {
      dateFilter.$lte = new Date(`${params.dateTo}T23:59:59.999+09:00`).toISOString();
    }
    const field =
      params.dateField === 'deliveredAt'
        ? 'gifticon.deliveredAt'
        : 'recipient.requestedAt';
    filter[field] = dateFilter;
  }

  return `/${LowerRole.Admin}/gift-coupon/search?${queryString({
    filter,
    populate: giftCouponPopulate,
    options: { sort: { createdAt: -1 } },
    ...(params.ordererName ? { ordererName: params.ordererName } : {}),
    ...(params.ordererPhone ? { ordererPhone: params.ordererPhone } : {}),
  })}`;
};

export const useGiftCouponSearchHook = (
  searchKey: string,
  params: GiftCouponSearchParams,
  skip = false,
) => {
  useRecoilQuery(
    giftCouponSearchStatusAtomFamily(searchKey),
    giftCouponSearchDataAtomFamily(searchKey),
    giftCouponSearchUrl(params),
    fetcher,
    skip,
  );
};
